import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { usePayrollBoard, useSmig } from "../../lib/api";
import { Money, SectionCard, Stat } from "../labour/shared";

/** What payroll covers, what it costs, and the depth it is missing (HRX-07).
 *
 * The backlog expected this to be empty. It is not: six months of 2026 are closed, 593 submitted slips across
 * 102 people, and the components carry the right DRC form. So this screen does not report an absence — it
 * reports four specific gaps around a payroll that works. */
export default function Runs({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = usePayrollBoard(company || undefined);
  const smig = useSmig(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.depth.filter((s) => !s.ok);

  return (
    <div className="mt-4 space-y-4" data-payroll-runs>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-payroll-strip>
        <Stat label={t("pay.lastRun")} value={<Money value={d.period.net} lng={lng} />}
          hint={t("pay.lastRunHint", { n: d.period.emps, from: d.period.from })} />
        <Stat label={t("pay.gross")} value={<Money value={d.period.gross} lng={lng} />} hint={t("pay.grossHint")} />
        <Stat label={t("pay.covered")} value={`${d.coverage.paid}/${d.coverage.active}`}
          cls={d.coverage.covered_pct < 95 ? "text-status-amber" : "text-status-green"}
          hint={t("pay.coveredHint", { pct: d.coverage.covered_pct })} />
        <Stat label={t("pay.gratuityLiability")} value={<Money value={d.gratuity.amount} lng={lng} />}
          cls={d.gratuity.amount ? "text-status-amber" : ""}
          hint={t("pay.gratuityLiabilityHint", { n: d.gratuity.qualifying })} />
        <Stat label={t("pay.gapsOpen")} value={failing.length}
          cls={failing.length ? "text-status-amber" : "text-status-green"} hint={t("pay.ofChecks", { n: d.depth.length })} />
      </div>

      <SectionCard title={t("pay.depth")} aside={t("pay.depthN", { n: failing.length, of: d.depth.length })}>
        <ul>
          {d.depth.map((s) => (
            <li key={s.key} className="flex items-start gap-2 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
              data-payroll-check={s.key} data-payroll-check-ok={s.ok ? "1" : "0"}>
              <span className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${s.ok ? "bg-status-green" : "bg-status-amber"}`} />
              <div>
                <div className="font-semibold">{t(`pay.check.${s.key}.title`)}</div>
                <p className="mt-0.5 max-w-4xl text-sm text-brand-grey">
                  {t(`pay.check.${s.key}.${s.ok ? "ok" : "bad"}`, s.facts) as string}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title={t("pay.coverage")} aside={t("pay.since", { from: d.all_time.n })}>
          <div className="grid grid-cols-2 gap-4 p-4">
            <Fact label={t("pay.active")} value={d.coverage.active} />
            <Fact label={t("pay.assigned")} value={d.coverage.assigned} />
            <Fact label={t("pay.paidLastRun")} value={d.coverage.paid} />
            <Fact label={t("pay.unassigned")} value={d.coverage.unassigned}
              tone={d.coverage.unassigned ? "amber" : undefined} />
          </div>
          {d.companies_without_anybody_assigned.length > 0 && (
            <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey"
              data-payroll-empty-companies>
              {t("pay.emptyCompanies", { list: d.companies_without_anybody_assigned.join(", ") })}
            </p>
          )}
        </SectionCard>

        <SectionCard title={t("pay.allTime")} aside={t("pay.slipsN", { n: d.all_time.n })}>
          <div className="grid grid-cols-2 gap-4 p-4">
            <Fact label={t("pay.gross")} value={<Money value={d.all_time.gross} lng={lng} />} />
            <Fact label={t("pay.deductions")} value={<Money value={d.all_time.deductions} lng={lng} />} />
            <Fact label={t("pay.net")} value={<Money value={d.all_time.net} lng={lng} />} />
            <Fact label={t("pay.people")} value={d.all_time.emps} />
          </div>
          <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey">
            {t("pay.basisNote", { basis: d.basis.payroll_based_on ?? "—",
                                  unmarked: d.basis.unmarked_attendance_as ?? "—",
                                  days: d.basis.attendance_days_recorded })}
          </p>
        </SectionCard>
      </div>
      <SectionCard title={t("smig.title")}
        aside={smig.data ? t("smig.floorIs", { amount: smig.data.floor.monthly.toLocaleString(),
          daily: smig.data.floor.daily.toLocaleString(), days: smig.data.floor.days }) : undefined}>
        <p className="border-b border-brand-grey-light px-4 py-2 text-[12px] text-brand-grey">{t("smig.hint")}</p>
        {!smig.data ? (
          <div className="p-4"><Skeleton className="h-16" /></div>
        ) : (
          <div className="p-4 text-[12px]" data-payroll-smig>
            <div className="flex flex-wrap gap-5">
              <span><strong className={smig.data.below_count ? "text-status-red" : "text-status-green"}>
                {smig.data.below_count}</strong> {t("smig.below")}</span>
              <span><strong>{smig.data.at_or_above_count}</strong> {t("smig.above")}</span>
              <span><strong className={smig.data.no_base_count ? "text-status-amber" : ""}>
                {smig.data.no_base_count}</strong> {t("smig.noBase")}</span>
              {smig.data.shortfall > 0 && (
                <span className="text-status-red">{t("smig.shortfall")}: <Money value={smig.data.shortfall} lng={lng} /></span>
              )}
            </div>
            {smig.data.below_count > 0 && (
              <ul className="mt-3 space-y-1" data-payroll-smig-below>
                {smig.data.below.slice(0, 10).map((p) => (
                  <li key={p.employee}>
                    {p.employee_name} <span className="text-brand-grey">· {p.company}</span>
                    {" — "}<Money value={p.short_by} lng={lng} /> {t("smig.shortBy")}
                  </li>
                ))}
              </ul>
            )}
            {smig.data.no_base_count > 0 && (
              <p className="mt-3 text-status-amber" data-payroll-smig-nobase>
                {t("smig.noBaseWhy", { n: smig.data.no_base_count })}
              </p>
            )}
          </div>
        )}
      </SectionCard>

    </div>
  );
}

function Fact({ label, value, tone }: { label: string; value: React.ReactNode; tone?: "amber" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      <div className={`font-heading text-lg font-semibold ${tone === "amber" ? "text-status-amber" : ""}`}>{value}</div>
    </div>
  );
}
